/**
 * @module category.transformer
 */
import { defaultLang, supportedLanguages } from '~/config/settings/app.settings';
import { text } from '~/tools/text';

/**
 * Transform Category "names" array to translations dictionary.
 *
 * @param {Object} obj - destructured param object
 * @param {Array} obj.names - a set of "name/lang" objects from the server
 * @returns {Object} a dictionary of translated strings
 *
 * @see `/src/tools/propTypes` for example translations shape.
 *
 * @example names (from server)
 * names: [
 *   {
 *     "languageId": 1,
 *     "languageCode": "en-ca",
 *     "name": "Leadership"
 *   }
 * ]
 */
const categoryTranslationsFrom = ({ names = [] }) => {
  const translations = supportedLanguages.reduce((dict, lang) => {
    // @ts-expect-error TS(2339): Property 'languageCode' does not exist on type 'ne... Remove this comment to see the full error message
    const nameObj = names.find((n) => n.languageCode === lang);

    if (!nameObj) return dict;

    // A collection of all translated strings for the given `lang`.
    const translation = {
      // @ts-expect-error TS(2339): Property 'name' does not exist on type 'never'.
      name: nameObj?.name,
    };

    return {
      ...dict,
      [lang]: translation,
    };
  }, {});

  return translations;
};

/**
 * Transform translation objects into Cateogry "names" array.
 *
 * @param {Object} translations - a dictionary of translated strings
 * @param {Object} languageCodeIds - a dictionary that maps ISO codes to database IDs
 * @returns {Array} an array of "name/lang" objects formatted for the server
 *
 * @see ./language.transformer.js
 */
const categoryTranslationsToNames = (translations, languageCodeIds) => {
  if (!translations) return [];

  const names = Object.keys(translations).map((lang) => ({
    languageId: languageCodeIds[lang],
    languageCode: lang,
    name: translations[lang].name,
  }));

  return names;
};

/**
 * Create "category" entities for the front-end, appending virtual values that
 * can be calculated up-front, here.
 *
 * @example category (from server)
 * {
 *   "id": 1,
 *   "name": "Leadership"
 *   "names": [
 *     {
 *       "languageId": 1,
 *       "languageCode": "en-ca",
 *       "name": "Leadership"
 *     }
 *   ],
 *   "isKeyFactor": null,
 *   "isBaseCategory": true,
 *   "baseCategoryId": 123
 * }
 */
const categoryFrom = (category = {}, surveyTypeId = null) => {
  const translations = categoryTranslationsFrom({
    // @ts-expect-error TS(2339): Property 'names' does not exist on type '{}'.
    names: category.names,
  });

  const defaultName =
    // @ts-expect-error TS(2339): Property 'name' does not exist on type '{}'.
    category.name || translations[defaultLang]?.name || text.findFirstTranslationFor(translations, 'name');

  const transformedCategory = {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type '{}'.
    id: category.id,
    // @ts-expect-error TS(2339): Property 'baseCategoryId' does not exist on type '... Remove this comment to see the full error message
    baseCategoryId: category.baseCategoryId,
    // @ts-expect-error TS(2339): Property 'surveyTypeId' does not exist on type '{}... Remove this comment to see the full error message
    surveyTypeId: category.surveyTypeId || surveyTypeId,
    name: defaultName,
    translations,
    // @ts-expect-error TS(2339): Property 'isKeyFactor' does not exist on type '{}'... Remove this comment to see the full error message
    isKeyFactor: category.isKeyFactor,
    // @ts-expect-error TS(2339): Property 'isBaseCategory' does not exist on type '... Remove this comment to see the full error message
    isBaseCategory: category.isBaseCategory,

    // DEPRECATED: use translations instead
    // @ts-expect-error TS(2339): Property 'names' does not exist on type '{}'.
    names: category.names.map((name) => ({
      languageId: name.languageId,
      lang: name.languageCode,
      value: name.name,
    })),
  };

  return transformedCategory;
};

const categoriesFrom = (categories = []) => {
  return categories.map((category) => categoryFrom(category));
};

export { categoryFrom, categoriesFrom, categoryTranslationsFrom, categoryTranslationsToNames };
